import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Illustration, Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useHistory, useLocation } from "react-router-dom";

import {
  SHIFT_DISCOVERY_LIST_PATH,
  SHIFT_DISCOVERY_SHIFT_MODAL_PATH,
} from "../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../ShiftDiscovery/useShiftModalsDataContext";
import { SHIFT_INVITES_PATH } from "../WorkerShifts/path";
import { BackgroundCheckPageContainer } from "./BackgroundCheckPageContainer";
import { RETURN_TO } from "./constants";

interface BackgroundCheckCompletedPageProps {
  shiftId?: string;
  hideHeader?: boolean;
  returnTo?: string;
}

export function BackgroundCheckCompletedPage(props: BackgroundCheckCompletedPageProps) {
  const { hideHeader = false, shiftId, returnTo } = props;
  const { navigateToModal } = useShiftModalsDataContext();
  const history = useHistory();
  const location = useLocation();

  useLogEffect(APP_V2_APP_EVENTS.BACKGROUND_CHECK_COMPLETED_PAGE_VIEWED);

  return (
    <BackgroundCheckPageContainer
      buttonText="Continue booking shifts"
      hideHeader={hideHeader}
      onButtonClick={() => {
        if (isDefined(location.key)) {
          // If they're on the background check completed page, we can just go back since
          // we use `replace` instead of `push` when navigating the background check pages.
          history.goBack();
          return;
        }

        // Ideally we always have the previous location. It is not clear when the
        // location.key might be `undefined`. Even for deep-links and hard refresh
        // they seem to be defined. However, the code below serves as a defensive check if the
        // `location.key` is `undefined`. In such cases, we redirect the user to the most
        // appropriate page.

        if (isDefined(returnTo) && returnTo === RETURN_TO.SHIFT_INVITE) {
          // We're replacing instead of pushing because if they try to go back
          // we don't want them to land on background check pages again.
          history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_INVITES_PATH}`);
          return;
        }

        if (!isDefined(shiftId)) {
          // We're replacing instead of pushing because if they try to go back
          // we don't want them to land on background check pages again.
          history.replace(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
          return;
        }

        navigateToModal(SHIFT_DISCOVERY_SHIFT_MODAL_PATH, {
          pathParams: { shiftId },
          clearExistingSearchParams: true,
        });
      }}
    >
      <Stack spacing={8} sx={{ alignItems: "center", textAlign: "center", mb: 8 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Illustration type="check" />
        </Box>

        <Stack spacing={4}>
          <Title variant="h2" component="h2">
            You&apos;re all set!
          </Title>
          <Text variant="h6">
            Your background check is processing — most are completed within a couple of hours. You
            can continue booking shifts while it&apos;s processing.
          </Text>
          <Text variant="h6">
            If it takes longer or needs review, we&apos;ll update you immediately about any booked
            shifts and next steps.
          </Text>
        </Stack>
      </Stack>
    </BackgroundCheckPageContainer>
  );
}
